<template>
  <div class="home-page">
    <header class="home-header">
      <h1>{{ $t('banner.hotDramaTitle') }}</h1>

      <button class="search-entry" type="button" @click="goSearch">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="11" cy="11" r="6.5" />
          <path d="m16 16 4 4" />
        </svg>
        <span>{{ $t('search.placeInputHolder') }}</span>
      </button>
    </header>

    <section v-if="featuredDramas.length" class="hero-section">
      <div class="hero-viewport">
        <div
          class="hero-track"
          :style="{ transform: `translateX(-${activeSlide * 100}%)` }"
        >
          <article
            v-for="item in featuredDramas"
            :key="item.id"
            class="hero-slide"
            @click="goDetailVideo(item)"
          >
            <img :src="item.img" :alt="item.name" />
            <div class="hero-shade"></div>
            <div class="hero-copy">
              <span class="hero-label">{{ $t('video.totalEpisodes', { num: item.sum }) }}</span>
              <h2>{{ item.name }}</h2>
              <button type="button">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="m9 6 9 6-9 6V6Z" />
                </svg>
                {{ $t('history.continue') }}
              </button>
            </div>
          </article>
        </div>
      </div>

      <div class="hero-dots">
        <button
          v-for="(item, index) in featuredDramas"
          :key="`dot-${item.id}`"
          type="button"
          :class="{ active: activeSlide === index }"
          :aria-label="`Slide ${index + 1}`"
          @click="setActiveSlide(index)"
        ></button>
      </div>
    </section>

    <section class="drama-section">
      <div class="section-header">
        <h2>{{ $t('banner.hotDramaTitle') }}</h2>
        <span>{{ videoLists.length }}</span>
      </div>

      <div v-if="loading && !videoLists.length" class="loading-grid">
        <div v-for="item in 6" :key="item" class="loading-card"></div>
      </div>

      <div v-else class="drama-grid">
        <article
          v-for="item in videoLists"
          :key="item.id"
          class="drama-card"
          @click="goDetailVideo(item)"
        >
          <div class="drama-cover">
            <img :src="item.img" :alt="item.name" loading="lazy" />
            <span>{{ item.sum }}</span>
          </div>
          <h3>{{ item.name }}</h3>
        </article>
      </div>

      <div v-if="!loading && !videoLists.length" class="empty-state">
        {{ $t('search.noWorks') }}
      </div>
    </section>

    <tab-bar currentNav="home" />
  </div>
</template>

<script>
import TabBar from '@/components/tabBar.vue';
import { videoList } from '@/apis/video';

export default {
  name: 'HomeIndex',
  components: {
    TabBar
  },
  data() {
    return {
      videoLists: [],
      featuredDramas: [],
      activeSlide: 0,
      carouselTimer: null,
      loading: false,
      query: {
        limit: 10000,
        page: 1
      }
    };
  },
  mounted() {
    this.getList();
  },
  activated() {
    this.startCarousel();
  },
  deactivated() {
    this.stopCarousel();
  },
  beforeDestroy() {
    this.stopCarousel();
  },
  methods: {
    async getList() {
      this.loading = true;

      try {
        const data = await videoList({
          limit: this.query.limit,
          offset: this.query.page,
          lx: 1,
          typeid: 66
        });

        const rows = Array.isArray(data.rows) ? data.rows : [];
        this.videoLists = rows;
        this.featuredDramas = this.pickRandom(rows, 5);
        this.activeSlide = 0;
        this.startCarousel();

        if (data.config) {
          localStorage.setItem('config', JSON.stringify(data.config));
        }
      } catch (error) {
        console.error('获取列表失败', error);
        this.videoLists = [];
        this.featuredDramas = [];
      } finally {
        this.loading = false;
      }
    },
    pickRandom(list, count) {
      const shuffled = list.slice();

      for (let index = shuffled.length - 1; index > 0; index -= 1) {
        const randomIndex = Math.floor(Math.random() * (index + 1));
        [shuffled[index], shuffled[randomIndex]] = [shuffled[randomIndex], shuffled[index]];
      }

      return shuffled.slice(0, count);
    },
    startCarousel() {
      this.stopCarousel();
      if (this.featuredDramas.length < 2) return;

      this.carouselTimer = window.setInterval(() => {
        this.activeSlide = (this.activeSlide + 1) % this.featuredDramas.length;
      }, 4500);
    },
    stopCarousel() {
      if (!this.carouselTimer) return;
      window.clearInterval(this.carouselTimer);
      this.carouselTimer = null;
    },
    setActiveSlide(index) {
      this.activeSlide = index;
      this.startCarousel();
    },
    goSearch() {
      this.$router.push('/search/search?autoFocus=true');
    },
    goDetailVideo(data) {
      let fxpid = 1;
      const userinfo = this.$getUserInfo();
      if (userinfo) {
        fxpid = userinfo.id;
      }
      this.$router.push(`/video/details/details?vid=${data.id}&mid=0&fxpid=${fxpid}`);
    }
  }
};
</script>

<style scoped>
.home-page {
  width: 100%;
  max-width: 450px;
  min-height: 100vh;
  margin: 0 auto;
  padding-bottom: 86px;
  background: #f5f5f7;
  color: #1d1d1f;
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", sans-serif;
  box-sizing: border-box;
}

.home-header {
  padding: 24px 16px 14px;
}

.home-header h1 {
  margin: 0 0 16px;
  font-size: 32px;
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: -0.045em;
}

.search-entry {
  display: flex;
  align-items: center;
  width: 100%;
  height: 38px;
  padding: 0 12px;
  gap: 8px;
  border: 0;
  border-radius: 11px;
  background: #e8e8ed;
  color: #8e8e93;
  font: inherit;
  font-size: 14px;
  text-align: left;
}

.search-entry:active {
  background: #dedee3;
}

.search-entry svg {
  width: 18px;
  height: 18px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
}

.hero-section {
  margin-bottom: 26px;
}

.hero-viewport {
  margin: 0 16px;
  overflow: hidden;
  border-radius: 24px;
  background: #1d1d1f;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.16);
}

.hero-track {
  display: flex;
  transition: transform 0.55s cubic-bezier(0.32, 0.72, 0, 1);
}

.hero-slide {
  position: relative;
  flex: 0 0 100%;
  height: 250px;
  overflow: hidden;
}

.hero-slide > img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.hero-shade {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, transparent 30%, rgba(0, 0, 0, 0.84) 100%);
}

.hero-copy {
  position: absolute;
  right: 18px;
  bottom: 18px;
  left: 18px;
  color: #fff;
}

.hero-label {
  display: block;
  margin-bottom: 6px;
  color: rgba(255, 255, 255, 0.74);
  font-size: 11px;
}

.hero-copy h2 {
  max-width: 90%;
  margin: 0 0 12px;
  overflow: hidden;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.16;
  letter-spacing: -0.035em;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.hero-copy button {
  display: inline-flex;
  align-items: center;
  height: 32px;
  padding: 0 13px;
  gap: 5px;
  border: 0;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.94);
  color: #1d1d1f;
  font: inherit;
  font-size: 11px;
  font-weight: 600;
}

.hero-copy button svg {
  width: 13px;
  height: 13px;
  fill: currentColor;
}

.hero-dots {
  display: flex;
  justify-content: center;
  margin-top: 11px;
  gap: 5px;
}

.hero-dots button {
  width: 5px;
  height: 5px;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: #c7c7cc;
  transition: width 0.25s ease, background 0.25s ease;
}

.hero-dots button.active {
  width: 18px;
  background: #1d1d1f;
}

.drama-section {
  padding: 0 16px;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}

.section-header h2 {
  margin: 0;
  font-size: 21px;
  font-weight: 700;
  letter-spacing: -0.03em;
}

.section-header span {
  color: #8e8e93;
  font-size: 12px;
}

.drama-grid,
.loading-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px 12px;
}

.drama-card {
  min-width: 0;
}

.drama-card:active {
  transform: scale(0.98);
}

.drama-cover {
  position: relative;
  aspect-ratio: 4 / 5;
  overflow: hidden;
  border-radius: 16px;
  background: #e5e5ea;
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.07);
}

.drama-cover img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.drama-cover span {
  position: absolute;
  right: 7px;
  bottom: 7px;
  min-width: 22px;
  padding: 4px 7px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.65);
  color: #fff;
  font-size: 9px;
  text-align: center;
  backdrop-filter: blur(8px);
}

.drama-card h3 {
  display: -webkit-box;
  min-height: 37px;
  margin: 9px 2px 0;
  overflow: hidden;
  color: #1d1d1f;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.4;
  letter-spacing: -0.01em;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.loading-card {
  aspect-ratio: 4 / 5.5;
  border-radius: 16px;
  background: linear-gradient(100deg, #e5e5ea 30%, #f2f2f7 50%, #e5e5ea 70%);
  background-size: 300% 100%;
  animation: loading-shimmer 1.4s infinite;
}

.empty-state {
  padding: 70px 0;
  color: #8e8e93;
  font-size: 13px;
  text-align: center;
}

@keyframes loading-shimmer {
  to {
    background-position: -150% 0;
  }
}
</style>
